import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useJobAnalysis } from '../hooks/useJobAnalysis';
import type { JobAnalysisResponse, Confidence, Recommendation } from '../types';

interface JobAnalyzerProps {
  onAnalysisComplete?: (result: JobAnalysisResponse) => void;
  className?: string;
}

type Tab = 'analysis' | 'letter';

const getScoreColor = (score: number): string => {
  if (score >= 70) return '#1FCF5E';
  if (score >= 50) return '#ffc107';
  return '#ff6b6b';
};

const getRecommendationStyle = (rec: Recommendation): { bg: string; text: string } => {
  switch (rec) {
    case 'Søk':
      return { bg: 'rgba(31, 207, 94, 0.2)', text: '#1FCF5E' };
    case 'Vurder':
      return { bg: 'rgba(255, 193, 7, 0.2)', text: '#ffc107' };
    case 'Unngå':
      return { bg: 'rgba(255, 107, 107, 0.2)', text: '#ff6b6b' };
    default:
      return { bg: 'rgba(136, 136, 136, 0.2)', text: '#888' };
  }
};

const getConfidenceStyle = (conf: Confidence): { bg: string; text: string } => {
  switch (conf) {
    case 'HIGH':
      return { bg: 'rgba(31, 207, 94, 0.15)', text: '#1FCF5E' };
    case 'MEDIUM':
      return { bg: 'rgba(255, 193, 7, 0.15)', text: '#ffc107' };
    case 'TRANSFERABLE':
      return { bg: 'rgba(100, 149, 237, 0.15)', text: '#6495ED' };
    case 'LOW':
      return { bg: 'rgba(255, 107, 107, 0.15)', text: '#ff6b6b' };
    default:
      return { bg: 'rgba(136, 136, 136, 0.15)', text: '#888' };
  }
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div style={{ marginBottom: '16px' }}>
    <div
      style={{
        color: '#888',
        fontSize: '11px',
        textTransform: 'uppercase',
        marginBottom: '6px',
        letterSpacing: '0.5px',
      }}
    >
      {title}
    </div>
    {children}
  </div>
);

const JobAnalyzer: React.FC<JobAnalyzerProps> = ({ onAnalysisComplete, className = '' }) => {
  const { t } = useTranslation();
  const [jobText, setJobText] = useState('');
  const [activeTab, setActiveTab] = useState<Tab>('analysis');
  const { isLoading, result, error, analyzeJob, clearResult } = useJobAnalysis();

  const getRecommendationLabel = (rec: Recommendation): string => {
    return t(`modals.jobAnalysis.recommendations.${rec}`, rec);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!jobText.trim() || jobText.trim().length < 50 || jobText.length > 5000) {
      return;
    }

    setActiveTab('analysis');
    const analysisResult = await analyzeJob(jobText);
    if (analysisResult && onAnalysisComplete) {
      onAnalysisComplete(analysisResult);
    }
  };

  const handleClear = () => {
    setJobText('');
    setActiveTab('analysis');
    clearResult();
  };

  const copyToClipboard = async () => {
    if (result?.application_text) {
      await navigator.clipboard.writeText(result.application_text);
    }
  };

  const tabStyle = (isActive: boolean) => ({
    padding: '8px 16px',
    backgroundColor: isActive ? 'rgba(31, 207, 94, 0.15)' : 'transparent',
    color: isActive ? '#1FCF5E' : '#666',
    border: 'none',
    borderBottom: isActive ? '2px solid #1FCF5E' : '2px solid transparent',
    cursor: 'pointer',
    fontFamily: 'Courier New, monospace',
    fontSize: '13px',
    fontWeight: isActive ? 'bold' : 'normal',
  });

  return (
    <div className={`job-analyzer ${className}`}>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '8px',
            }}
          >
            <label htmlFor="job-text" style={{ color: '#1FCF5E', fontWeight: 'bold' }}>
              {t('modals.jobAnalysis.formLabel')}
            </label>
            <div
              style={{
                fontSize: '12px',
                fontFamily: 'Courier New, monospace',
                color:
                  jobText.length > 5000 ? '#ff6b6b' : jobText.length > 4500 ? '#ffc107' : '#1FCF5E',
              }}
            >
              [{jobText.length}/5000]
            </div>
          </div>
          <textarea
            id="job-text"
            value={jobText}
            onChange={e => setJobText(e.target.value)}
            placeholder={t('modals.jobAnalysis.placeholder')}
            rows={8}
            disabled={isLoading}
            style={{
              width: '100%',
              padding: '12px',
              border: `1px solid ${jobText.length > 5000 ? 'rgba(255, 107, 107, 0.5)' : 'rgba(31, 207, 94, 0.3)'}`,
              borderRadius: '4px',
              fontSize: '14px',
              fontFamily: 'Courier New, monospace',
              resize: 'vertical',
              minHeight: '120px',
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              color: '#1FCF5E',
            }}
          />
          {jobText.length > 5000 && (
            <div
              style={{
                marginTop: '4px',
                fontSize: '12px',
                color: '#ff6b6b',
                fontFamily: 'Courier New, monospace',
              }}
            >
              {t('modals.jobAnalysis.charLimitExceeded')} {jobText.length - 5000}
            </div>
          )}
        </div>

        <div className="form-actions" style={{ marginTop: '16px', display: 'flex', gap: '12px' }}>
          <button
            type="submit"
            disabled={
              isLoading || !jobText.trim() || jobText.trim().length < 50 || jobText.length > 5000
            }
            style={{
              padding: '8px 16px',
              backgroundColor:
                isLoading || !jobText.trim() || jobText.trim().length < 50 || jobText.length > 5000
                  ? 'rgba(31, 207, 94, 0.3)'
                  : '#1FCF5E',
              color:
                isLoading || !jobText.trim() || jobText.trim().length < 50 || jobText.length > 5000
                  ? 'rgba(255, 255, 255, 0.5)'
                  : '#ffffff',
              border: '1px solid #1FCF5E',
              borderRadius: '4px',
              cursor:
                isLoading || !jobText.trim() || jobText.trim().length < 50 || jobText.length > 5000
                  ? 'not-allowed'
                  : 'pointer',
              fontFamily: 'Courier New, monospace',
              fontWeight: 'bold',
            }}
          >
            {isLoading ? t('modals.jobAnalysis.analyzing') : t('modals.jobAnalysis.analyzeBtn')}
          </button>

          <button
            type="button"
            onClick={handleClear}
            disabled={isLoading}
            style={{
              padding: '8px 16px',
              backgroundColor: 'rgba(31, 207, 94, 0.2)',
              color: '#1FCF5E',
              border: '1px solid rgba(31, 207, 94, 0.5)',
              borderRadius: '4px',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontFamily: 'Courier New, monospace',
              fontWeight: 'bold',
            }}
          >
            {t('modals.jobAnalysis.resetBtn')}
          </button>
        </div>
      </form>

      {isLoading && (
        <div
          style={{
            marginTop: '20px',
            padding: '16px',
            backgroundColor: 'rgba(31, 207, 94, 0.05)',
            border: '1px solid rgba(31, 207, 94, 0.2)',
            borderRadius: '4px',
            color: '#1FCF5E',
            fontFamily: 'Courier New, monospace',
            fontSize: '14px',
            textAlign: 'center',
          }}
        >
          {t('modals.jobAnalysis.analyzingJob')}
        </div>
      )}

      {error && (
        <div
          className="error-message"
          style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: 'rgba(255, 0, 0, 0.1)',
            border: '1px solid rgba(255, 0, 0, 0.3)',
            borderRadius: '4px',
            color: '#ff6b6b',
          }}
        >
          <strong>{t('modals.jobAnalysis.error')}</strong> {error}
        </div>
      )}

      {result && (
        <div
          className="analysis-result"
          style={{
            marginTop: '20px',
            fontFamily: 'Courier New, monospace',
            fontSize: '13px',
            lineHeight: '1.5',
          }}
        >
          {/* Tabs */}
          <div
            style={{
              display: 'flex',
              borderBottom: '1px solid rgba(31, 207, 94, 0.2)',
              marginBottom: '0',
            }}
          >
            <button
              type="button"
              onClick={() => setActiveTab('analysis')}
              style={tabStyle(activeTab === 'analysis')}
            >
              {t('modals.jobAnalysis.tabs.analysis')}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('letter')}
              style={tabStyle(activeTab === 'letter')}
            >
              {t('modals.jobAnalysis.tabs.letter')}
            </button>
          </div>

          {/* Tab Content */}
          <div
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              border: '1px solid rgba(31, 207, 94, 0.3)',
              borderTop: 'none',
              borderRadius: '0 0 4px 4px',
              padding: '20px',
            }}
          >
            {activeTab === 'analysis' && (
              <>
                {/* Score Header */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    marginBottom: '20px',
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                    <span
                      style={{
                        fontSize: '32px',
                        fontWeight: 'bold',
                        color: getScoreColor(result.score),
                      }}
                    >
                      {result.score}
                    </span>
                    <span style={{ color: '#666', fontSize: '14px' }}>/100</span>
                  </div>

                  <div
                    style={{
                      padding: '4px 12px',
                      backgroundColor: getRecommendationStyle(result.recommendation).bg,
                      color: getRecommendationStyle(result.recommendation).text,
                      borderRadius: '4px',
                      fontWeight: 'bold',
                      fontSize: '12px',
                    }}
                  >
                    {getRecommendationLabel(result.recommendation)}
                  </div>

                  {result.level && (
                    <div style={{ color: '#888', fontSize: '12px' }}>{result.level}</div>
                  )}
                </div>

                {/* Summary */}
                <Section title={t('modals.jobAnalysis.sections.summary')}>
                  <div style={{ color: '#ccc' }}>{result.summary}</div>
                </Section>

                {/* Strengths & Concerns */}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '16px',
                    marginBottom: '16px',
                  }}
                >
                  {result.strengths.length > 0 && (
                    <Section title={t('modals.jobAnalysis.sections.strengths')}>
                      <ul style={{ margin: 0, paddingLeft: '16px', color: '#1FCF5E' }}>
                        {result.strengths.map((s, i) => (
                          <li key={i} style={{ marginBottom: '4px' }}>
                            {s}
                          </li>
                        ))}
                      </ul>
                    </Section>
                  )}
                  {result.concerns.length > 0 && (
                    <Section title={t('modals.jobAnalysis.sections.concerns')}>
                      <ul style={{ margin: 0, paddingLeft: '16px', color: '#ffc107' }}>
                        {result.concerns.map((c, i) => (
                          <li key={i} style={{ marginBottom: '4px' }}>
                            {c}
                          </li>
                        ))}
                      </ul>
                    </Section>
                  )}
                </div>

                {/* Skills Found */}
                {result.skills.found.length > 0 && (
                  <Section title={t('modals.jobAnalysis.sections.foundSkills')}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {result.skills.found.map((skill, i) => (
                        <div
                          key={i}
                          title={skill.evidence}
                          style={{
                            padding: '4px 10px',
                            backgroundColor: getConfidenceStyle(skill.confidence).bg,
                            color: getConfidenceStyle(skill.confidence).text,
                            borderRadius: '4px',
                            fontSize: '12px',
                            cursor: 'help',
                          }}
                        >
                          {skill.skill}
                        </div>
                      ))}
                    </div>
                  </Section>
                )}

                {/* Skills Missing */}
                {result.skills.missing.length > 0 && (
                  <Section title={t('modals.jobAnalysis.sections.missingSkills')}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {result.skills.missing.map((skill, i) => (
                        <div
                          key={i}
                          title={skill.reason}
                          style={{
                            padding: '4px 10px',
                            backgroundColor: 'rgba(255, 107, 107, 0.1)',
                            color: '#ff6b6b',
                            borderRadius: '4px',
                            fontSize: '12px',
                            cursor: 'help',
                            border: '1px dashed rgba(255, 107, 107, 0.3)',
                          }}
                        >
                          {skill.skill}
                        </div>
                      ))}
                    </div>
                  </Section>
                )}

                {/* Relevant Experience */}
                {result.relevant_experience.length > 0 && (
                  <Section title={t('modals.jobAnalysis.sections.experience')}>
                    <ul style={{ margin: 0, paddingLeft: '16px', color: '#ccc' }}>
                      {result.relevant_experience.map((exp, i) => (
                        <li key={i} style={{ marginBottom: '4px' }}>
                          {exp}
                        </li>
                      ))}
                    </ul>
                  </Section>
                )}

                {/* Requirements */}
                {(result.requirements.must_haves.length > 0 ||
                  result.requirements.nice_to_haves.length > 0) && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    {result.requirements.must_haves.length > 0 && (
                      <Section title={t('modals.jobAnalysis.sections.mustHaves')}>
                        <ul style={{ margin: 0, paddingLeft: '16px', color: '#ccc' }}>
                          {result.requirements.must_haves.map((req, i) => (
                            <li key={i} style={{ marginBottom: '4px' }}>
                              {req}
                            </li>
                          ))}
                        </ul>
                      </Section>
                    )}
                    {result.requirements.nice_to_haves.length > 0 && (
                      <Section title={t('modals.jobAnalysis.sections.niceToHaves')}>
                        <ul style={{ margin: 0, paddingLeft: '16px', color: '#888' }}>
                          {result.requirements.nice_to_haves.map((req, i) => (
                            <li key={i} style={{ marginBottom: '4px' }}>
                              {req}
                            </li>
                          ))}
                        </ul>
                      </Section>
                    )}
                  </div>
                )}

                {/* Footer */}
                <div
                  style={{
                    marginTop: '16px',
                    paddingTop: '12px',
                    borderTop: '1px solid rgba(31, 207, 94, 0.2)',
                    color: '#555',
                    fontSize: '11px',
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <span>
                    {result.requirements.domain && `${result.requirements.domain}`}
                    {result.requirements.role_level && ` · ${result.requirements.role_level}`}
                  </span>
                  <span>{result.processing_time.toFixed(2)}s</span>
                </div>
              </>
            )}

            {activeTab === 'letter' && (
              <>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
                  <button
                    type="button"
                    onClick={copyToClipboard}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: 'rgba(31, 207, 94, 0.1)',
                      color: '#1FCF5E',
                      border: '1px solid rgba(31, 207, 94, 0.3)',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontFamily: 'Courier New, monospace',
                      fontSize: '12px',
                    }}
                  >
                    {t('modals.jobAnalysis.copy')}
                  </button>
                </div>
                <div
                  style={{
                    color: '#ccc',
                    whiteSpace: 'pre-wrap',
                    lineHeight: '1.6',
                  }}
                >
                  {result.application_text}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default JobAnalyzer;
